// pages/CameraPage.tsx

import React, { useRef, useState, useEffect } from 'react';
import { Button, message, Spin, Result, Switch, Modal, Segmented, Badge } from 'antd';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  RetweetOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { ScanResult } from '../types/businessCard';
import axios from 'axios'; // Import axios for API requests
import { API_PATHS, getRoutePath } from '../config';
import { indexedDBStorage } from '../services/storage/indexedDB';
import { logApiError } from '../utils/errorLogger';
import { isInFlutter as isInFlutterUtil } from '../utils/flutterDetection';
import { ensureMediaPermission } from '../utils/mediaPermission';
import {
  getOcrModelPreference,
  setLastOcrUsedModel,
} from '../utils/ocrModel';
import MultiImageUploadFlow from '../components/MultiImageUploadFlow';
import ImagePreviewCarouselModal from '../components/ImagePreviewCarouselModal';
import { refreshCapturedEmergencyMode, getEmergencyMode } from '../utils/chatGptOcr';

type CaptureMode = 'single' | 'multi';
type CapturedItem = { original: string; extracted: string | null };


// windowにcvプロパティを追加するための型拡張
declare global {
  interface Window {
    cv: any;
    FlutterCamera?: {
      postMessage: (message: string) => void;
    };
    FlutterDebug?: {
      postMessage: (message: string) => void;
    };
    FlutterError?: {
      postMessage: (message: string) => void;
    };
  }
}

const CameraPage: React.FC = () => {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  
  const [loading, setLoading] = useState<boolean>(true);
  const [cameraActive, setCameraActive] = useState<boolean>(false);
  const [cameraPermissionDenied, setCameraPermissionDenied] = useState<boolean>(false);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [extractedImage, setExtractedImage] = useState<string | null>(null);
  const [autoCapture, setAutoCapture] = useState<boolean>(true); // デフォルトでON

  // 連続撮影モード関連
  const [captureMode, setCaptureMode] = useState<CaptureMode>('single');
  const captureModeRef = useRef<CaptureMode>('single');
  const [capturedImages, setCapturedImages] = useState<CapturedItem[]>([]);
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  // 連続撮影の確認画面（単発撮影の確認画面に相当）
  const [multiConfirm, setMultiConfirm] = useState<boolean>(false);

  // 緊急モード（ComSystemMapper の UseChatGPT が true）。true の間は連続撮影を選択不可にする。
  const [emergencyMode, setEmergencyMode] = useState<boolean>(false);
  // CameraPage に入った時に UseChatGPT を取得して緊急モードを「確定・保持」する。
  // 以降の編集/フォロー登録ページはこの保持値を参照し、再取得しない。
  useEffect(() => {
    (async () => {
      const useChatGpt = await refreshCapturedEmergencyMode();
      setEmergencyMode(useChatGpt);
      // 緊急モード中に連続撮影に入っていた場合は単発に戻す
      if (useChatGpt) setCaptureMode('single');
    })();
  }, []);

  // captureMode の最新値を ref に同期（非同期コールバック・Flutter イベント側で参照）
  useEffect(() => {
    captureModeRef.current = captureMode;
  }, [captureMode]);

  // Flutter環境の検出（共通関数を使用）
  const isInFlutter = isInFlutterUtil();
  const [cameraRequested, setCameraRequested] = useState<boolean>(false);
  const [flutterProcessing, setFlutterProcessing] = useState<boolean>(false);
  const [ocrError, setOcrError] = useState<string | null>(null);
  // アップロード（OCR）の中断用（タイムアウト／キャンセルボタン共通）
  const uploadAbortRef = useRef<AbortController | null>(null);
  const uploadCancelledByUserRef = useRef<boolean>(false);
  
  // 再撮影ボタンの処理
  const handleRetake = () => {
    setOcrError(null);
    setCameraRequested(false);
    
    // ネイティブカメラを再起動
    setTimeout(() => {
      let cameraLaunched = false;
      
      if (window.FlutterCamera?.postMessage) {
        window.FlutterCamera.postMessage('openCamera');
        cameraLaunched = true;
      } else if (window.FlutterDebug?.postMessage) {
        window.FlutterDebug.postMessage('openCamera');
        cameraLaunched = true;
      } else if (window.FlutterNavigation?.postMessage) {
        window.FlutterNavigation.postMessage('openCamera');
        cameraLaunched = true;
      }
      
      if (cameraLaunched) {
        setCameraRequested(true);
      } else {
        setOcrError('ネイティブカメラの再起動に失敗しました。');
      }
    }, 100);
  };
  
  // FlutterErrorチャンネルからのエラーメッセージを監視
  useEffect(() => {
    if (isInFlutter) {
      // カスタムイベントとして processImageError を監視
      const handleProcessImageError = (event: CustomEvent) => {
        if (event.detail && event.detail.error) {
          setOcrError(event.detail.error);
        }
      };
      
      window.addEventListener('processImageError', handleProcessImageError as EventListener);
      
      // FlutterErrorチャンネルが利用可能な場合の代替監視
      if (window.FlutterError) {
        const originalPostMessage = window.FlutterError.postMessage;
        window.FlutterError.postMessage = (message: string) => {
          if (message.includes('processImage error:')) {
            const errorMessage = message.replace('processImage error: ', '');
            setOcrError(errorMessage);
          }
          return originalPostMessage.call(window.FlutterError, message);
        };
      }
      
      return () => {
        window.removeEventListener('processImageError', handleProcessImageError as EventListener);
      };
    }
  }, [isInFlutter]);
  
  // Flutter環境でネイティブカメラを起動とWebカメラ無効化
  useEffect(() => {
    if (isInFlutter) {

      // getUserMediaを無効化してカメラ権限要求を防ぐ
      const originalGetUserMedia = navigator.mediaDevices?.getUserMedia;
      if (originalGetUserMedia) {
        navigator.mediaDevices.getUserMedia = async () => {
          throw new Error('Camera access blocked in Flutter environment');
        };
        
        // コンポーネントアンマウント時に元に戻す
        return () => {
          if (originalGetUserMedia) {
            navigator.mediaDevices.getUserMedia = originalGetUserMedia;
          }
        };
      }
      
      if (!cameraRequested && !capturedImage && !extractedImage) {
        setCameraRequested(true);
        
        // Flutter環境でネイティブカメラを起動（複数のチャンネルを試行）
        let cameraLaunched = false;
        
        if (window.FlutterCamera?.postMessage) {
          window.FlutterCamera.postMessage('openCamera');
          cameraLaunched = true;
        } else if (window.FlutterDebug?.postMessage) {
          window.FlutterDebug.postMessage('openCamera');
          cameraLaunched = true;
        } else if (window.FlutterNavigation?.postMessage) {
          window.FlutterNavigation.postMessage('openCamera');
          cameraLaunched = true;
        }
        
        if (!cameraLaunched) {
          setCameraRequested(false);
          setOcrError('ネイティブカメラの起動に失敗しました。アプリを再起動してください。');
        }
      }
    }
  }, []); // 空の依存配列でマウント時のみ実行
  
  // EditResultPageから戻ってきた時のネイティブカメラ起動
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const forceNative = urlParams.get('forceNative') === 'true';
    
    if (forceNative && isInFlutter) {
      if (window.FlutterDebug) {
        window.FlutterDebug.postMessage('forceNative detected in Flutter environment');
      }
      
      // Flutter環境でネイティブカメラを起動（複数のチャンネルを試行）
      let cameraLaunched = false;
      
      if (window.FlutterCamera?.postMessage) {
        if (window.FlutterDebug) {
          window.FlutterDebug.postMessage('Launching native camera via FlutterCamera');
        }
        window.FlutterCamera.postMessage('openCamera');
        cameraLaunched = true;
      } else if (window.FlutterDebug?.postMessage) {
        window.FlutterDebug.postMessage('openCamera');
        cameraLaunched = true;
      } else if (window.FlutterNavigation?.postMessage) {
        window.FlutterNavigation.postMessage('openCamera');
        cameraLaunched = true;
      }
      
      if (!cameraLaunched) {
        if (window.FlutterError) {
          window.FlutterError.postMessage('ERROR: No Flutter channels available for forceNative camera launch');
        }
      }
    } else {
      const reason = !forceNative ? 'forceNative=false' : 
                    !isInFlutter ? 'not in Flutter' : 'unknown';
      if (window.FlutterDebug) {
        window.FlutterDebug.postMessage('Native camera NOT launched: ' + reason);
      }
    }
  }, []); // 空の依存配列でマウント時のみ実行
  
  // Flutterからのカメラ結果を受信
  useEffect(() => {
    const handleCameraResult = async (event: CustomEvent) => {
      // カメラ結果を処理してナビゲート
      if (event.detail) {
        navigate(getRoutePath('/edit'));
      } else {
        // カメラがキャンセルされた場合
        const backSideCaptureMode = await indexedDBStorage.getItem('backSideCaptureMode');
        if (backSideCaptureMode === 'true') {
          await indexedDBStorage.removeItem('backSideCaptureMode');
          navigate(getRoutePath('/edit'), { replace: true });
        } else {
          navigate(getRoutePath('/menu'));
        }
      }
    };

    const handleCameraCancel = async () => {
      // 裏面撮影モードかチェック
      const backSideCaptureMode = await indexedDBStorage.getItem('backSideCaptureMode');
      if (backSideCaptureMode === 'true') {
        // 裏面撮影モードの場合はフラグを削除してEditResultPageに戻る
        await indexedDBStorage.removeItem('backSideCaptureMode');
        navigate(getRoutePath('/edit'), { replace: true });
      } else {
        // 通常の場合はメニューに戻る
        navigate(getRoutePath('/menu'));
      }
    };

    const handleProcessImage = async (event: CustomEvent) => {
      window.FlutterDebug?.postMessage('Process image event received');

      const imageData = event.detail?.imageData;
      window.FlutterDebug?.postMessage(`Image data received: ${imageData ? 'data exists' : 'null'}`);

      if (imageData) {
        // 連続撮影モード: OCR/遷移はせず画像を蓄積して再度ネイティブカメラを起動
        if (captureModeRef.current === 'multi') {
          window.FlutterDebug?.postMessage('Multi-capture mode: accumulating image');
          setCapturedImages((prev) => {
            const next = [...prev, { original: imageData, extracted: null }];
            message.success(`撮影しました (${next.length}枚)`);
            return next;
          });
          // 少し間を空けてネイティブカメラを再起動
          setTimeout(() => {
            if (window.FlutterCamera?.postMessage) {
              window.FlutterCamera.postMessage('openCamera');
            } else if (window.FlutterDebug?.postMessage) {
              window.FlutterDebug.postMessage('openCamera');
            }
          }, 300);
          return;
        }

        window.FlutterDebug?.postMessage('Setting captured image and processing immediately');

        // Flutter環境フラグを先に無効化してUIの再レンダリングを防ぐ
        window.FlutterDebug?.postMessage('Disabling Flutter flag before processing');
        window.FlutterCamera = undefined;

        // 画像データを一時的に設定（ただしstateは更新しない）
        window.FlutterDebug?.postMessage('Processing image directly without state update');

        // processImageを直接実行するための一時的な処理
        const processFlutterImage = async () => {
          window.FlutterDebug?.postMessage('processFlutterImage: 開始');
          try {
            // 裏面撮影モードかチェック
            const backSideCaptureMode = await indexedDBStorage.getItem('backSideCaptureMode');

            if (backSideCaptureMode === 'true') {
              // 裏面撮影モード：OCRをスキップして画像だけ保存
              message.loading('裏面画像を保存中...', 0);

              await indexedDBStorage.setItem('backSideImage', imageData);
              await indexedDBStorage.removeItem('backSideCaptureMode');

              message.destroy();
              message.success('裏面画像を取り込みました');
              navigate(getRoutePath('/edit'));
              return;
            }

            setFlutterProcessing(true);
            setProcessingImage(true);
            setLoading(true);
            setApiError(null);

            message.loading('画像を処理中...', 0);

            // 画像の外周を5px切り取る処理を適用
            // const finalImage = await cropImageBorder(imageData, 5);
            const finalImage = imageData;

            // AIOCR APIを呼び出す
            const response = await uploadImageToServer(finalImage);

            if (!response.success) {
              // OCRエラー時はエラーメッセージと再撮影ボタンを表示
              window.FlutterError?.postMessage('OCR API error: ' + response.message);
              message.destroy();
              setFlutterProcessing(false);
              setLoading(false);
              setProcessingImage(false);
              setOcrError(response.message || 'OCR処理に失敗しました。再度撮影してください。');
              return;
            }

            const result = response.data;
            const apiResponse = response.apiResponse;
            
            // 処理結果をScanResultの形式に変換
            let scanResult = {
              success: true,
              data: result,
              imageUrl: result.imageUrl || finalImage,
              apiResponse: apiResponse
            };

            if(result){
              scanResult.data.businesscardfrontimage = finalImage;
            }

            // 結果をIndexedDBに保存
            await indexedDBStorage.setItem('scanResult', JSON.stringify(scanResult));

            window.FlutterDebug?.postMessage('processFlutterImage: 処理完了、/editに遷移');
            message.destroy();
            message.success('画像の処理が完了しました');
            navigate(getRoutePath('/edit'));
            
          } catch (error: any) {
            if (uploadCancelledByUserRef.current) {
              // ユーザーによるキャンセル。handleCancelUpload 側で撮影画面へ戻し済み。
              uploadCancelledByUserRef.current = false;
              return;
            }
            window.FlutterError?.postMessage('processImage error: ' + error.message);
            console.error('processImage error:', error);
            // 原因追跡のためサーバーログにも記録する。
            // （例: 表面撮影中に backSideCaptureMode フラグの IndexedDB 読取が失敗した等を可視化）
            logApiError('CameraPage.processFlutterImage', error);
            message.destroy();
            setFlutterProcessing(false);
            setLoading(false);
            setProcessingImage(false);

            // 予期しないエラー時は再撮影ボタンを表示。
            // 例外メッセージ（"データの取得に失敗しました: backSideCaptureMode" など IndexedDB の
            // キー名を含む内部情報）をそのまま出すと利用者に意味が伝わらないため、分かりやすい
            // 文面に統一する（詳細は上の console / FlutterError / errorLogger に記録済み）。
            setOcrError('画像の処理に失敗しました。通信環境をご確認のうえ、もう一度撮影してください。');
          }
        };

        // 直接処理を実行
        window.FlutterDebug?.postMessage('processFlutterImage: 実行開始');
        processFlutterImage();
        
      } else {
        window.FlutterDebug?.postMessage('No image data received');
      }
    };

    if (isInFlutter) {
      window.addEventListener('cameraResult', handleCameraResult as unknown as EventListener);
      window.addEventListener('cameraCancel', handleCameraCancel as unknown as EventListener);
      window.addEventListener('processImage', handleProcessImage as unknown as EventListener);
    }

    return () => {
      if (isInFlutter) {
        window.removeEventListener('cameraResult', handleCameraResult as unknown as EventListener);
        window.removeEventListener('cameraCancel', handleCameraCancel as unknown as EventListener);
        window.removeEventListener('processImage', handleProcessImage as unknown as EventListener);
      }
    };
  }, [isInFlutter, navigate]);

  // カメラ起動フラグのリセット（コンポーネントマウント時）
  const [mountKey] = useState(() => Date.now());

  // 初期値を取得（IndexedDBから）- アプリ起動直後に実行
  useEffect(() => {
    // セッション内でカメラを起動したかのフラグ
    const cameraLaunchedKey = 'cameraLaunchedInCurrentSession';

    // マウント時にフラグをクリア（各訪問を新しいセッションとして扱う）
    sessionStorage.removeItem(cameraLaunchedKey);

    const alreadyLaunched = sessionStorage.getItem(cameraLaunchedKey) === 'true';

    // URLパラメータをチェック
    const urlParams = new URLSearchParams(window.location.search);
    const forceNative = urlParams.get('forceNative') === 'true';

    // Flutterアプリ内で、かつ未起動の場合にネイティブカメラを呼び出し
    if (isInFlutter && !alreadyLaunched && !capturedImage && !extractedImage) {
      // フラグを立てて二重起動を防止
      sessionStorage.setItem(cameraLaunchedKey, 'true');
      setCameraRequested(true);

      // Flutter環境でネイティブカメラを起動（複数のチャンネルを試行）
      let cameraLaunched = false;

      if (window.FlutterCamera?.postMessage) {
        window.FlutterCamera.postMessage('openCamera');
        cameraLaunched = true;
      } else if (window.FlutterDebug?.postMessage) {
        window.FlutterDebug.postMessage('openCamera');
        cameraLaunched = true;
      } else if (window.FlutterNavigation?.postMessage) {
        window.FlutterNavigation.postMessage('openCamera');
        cameraLaunched = true;
      }

      if (!cameraLaunched) {
        // カメラ起動に失敗した場合はフラグをリセット
        sessionStorage.removeItem(cameraLaunchedKey);
        setCameraRequested(false);
        setOcrError('ネイティブカメラの起動に失敗しました。アプリを再起動してください。');
      }
      return;
    }

    const fetchSettings = async () => {
      try {
        // 新しいフロー開始時に前回のフォローNoをクリア
        await indexedDBStorage.removeItem('currentFollowNo');

        // 自動撮影の設定を取得
        const savedAutoCapture = await indexedDBStorage.getItem('autoCapture');
        if (savedAutoCapture !== null) {
          setAutoCapture(savedAutoCapture === 'true');
        }

        // カメラの向きの設定を取得
        const savedCameraFacingMode = await indexedDBStorage.getItem('cameraFacingMode');
        if (savedCameraFacingMode !== null) {
          setCameraFacingMode(savedCameraFacingMode);
        }

      } catch (error) {
        console.error('Failed to load settings from IndexedDB:', error);
      }
    };

    fetchSettings();
  }, [isInFlutter]); // 依存配列を最小限に（画像状態の変化で再実行しない）


  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isTablet, setIsTablet] = useState<boolean>(false); // タブレット判定用
  const [processingImage, setProcessingImage] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  // 横向き検知用フラグ
  const [isLandscape, setIsLandscape] = useState<boolean>(window.innerWidth > window.innerHeight);
  const [cvLoaded, setCvLoaded] = useState<boolean>(false);
  const [hasMultipleCameras, setHasMultipleCameras] = useState<boolean>(false);
  const [cameraFacingMode, setCameraFacingMode] = useState<string>('environment'); // デフォルトは背面カメラ
  // 設定が読み込まれたことを追跡するstate
  const [settingsLoaded, setSettingsLoaded] = useState<boolean>(false);

  // モバイル/タブレット判定
  useEffect(() => {
    const checkDevice = () => {
      const userAgent = navigator.userAgent || navigator.vendor;

      // iPad OSの検出（iPadOS 13以降はMacOSとして報告される）
      const isIpadOS = (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
                     /iPad/i.test(userAgent) ||
                     /iPad/.test(navigator.platform);

      // タブレット判定（新しい判定ロジック）
      const isTabletDevice =
        isIpadOS || // iPadの場合
        (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent)) || // Androidタブレット
        (window.innerWidth >= 768 && window.innerHeight >= 768) || // 画面サイズによる判定
        (window.innerWidth >= 768 && /Intel Mac OS/i.test(userAgent) && navigator.maxTouchPoints > 1) || // 新しいiPad判定
        // 強制的にタブレットとして扱う
        window.innerWidth >= 600; // より小さい画面サイズでもタブレットとして扱う

      // モバイル全般判定（スマートフォン＋タブレット）
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|iOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) ||
                            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      if (window.innerWidth >= 768 || isIpadOS) {
        setIsTablet(true);
      } else {
        setIsTablet(isTabletDevice);
      }

      setIsMobile(isMobileDevice || isTabletDevice);
    };
    
    checkDevice();
    
    // 複数カメラがあるか確認
    const checkMultipleCameras = async () => {
      if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
        try {
          // カメラアクセス許可を先に取得しないとiOSではデバイス情報が正確に取得できない場合がある
          const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
          // 一時的にストリームを取得してすぐに解放
          stream.getTracks().forEach(track => track.stop());
          
          const devices = await navigator.mediaDevices.enumerateDevices();
          const videoDevices = devices.filter(device => device.kind === 'videoinput');
          
          // iPhoneやiPadなどのiOSデバイスは常に複数カメラを持つと仮定
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
          setHasMultipleCameras(videoDevices.length > 1 || isIOS);
        } catch (error) {
          console.error('カメラ検出エラー:', error);
          // エラーが発生した場合でも、iOSデバイスでは複数カメラがあると仮定
          const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
          setHasMultipleCameras(isIOS);
        }
      }
    };
    
    checkMultipleCameras();
  }, []);
  // 画面回転／リサイズを監視して縦横を判定
  useEffect(() => {
    const update = () => {
      // 横向きかどうかを設定
      const newIsLandscape = window.innerWidth > window.innerHeight;
      setIsLandscape(newIsLandscape);

      const userAgent = navigator.userAgent || navigator.vendor;

      // iPad OSの検出（iPadOS 13以降はMacOSとして報告される）
      const isIpadOS = (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ||
                     /iPad/i.test(userAgent) ||
                     /iPad/.test(navigator.platform);

      // タブレット判定（新しい判定ロジック）
      const isTabletDevice =
        isIpadOS || // iPadの場合
        (/Android/i.test(userAgent) && !/Mobile/i.test(userAgent)) || // Androidタブレット
        (window.innerWidth >= 768 && window.innerHeight >= 768) || // 画面サイズによる判定
        (window.innerWidth >= 768 && /Intel Mac OS/i.test(userAgent) && navigator.maxTouchPoints > 1) || // 新しいiPad判定
        window.innerWidth >= 600; // より小さい画面サイズでもタブレットとして扱う

      // モバイル全般判定（スマートフォン＋タブレット）
      const isMobileDevice = /Android|webOS|iPhone|iPad|iPod|iOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent) ||
                            (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

      // iPadまたは大きな画面サイズの場合は強制的にタブレット
      if (window.innerWidth >= 768 || isIpadOS) {
        setIsTablet(true);
      } else {
        setIsTablet(isTabletDevice);
      }

      setIsMobile(isMobileDevice || isTabletDevice);
    };
    
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);
  
  // カメラストリームを停止する関数
  const stopCamera = () => {
    if (streamRef.current) {
      try {
        // すべてのトラックを確実に停止
        const tracks = streamRef.current.getTracks();
        
        tracks.forEach(track => {
          try {
            track.stop();
          } catch (e) {
            console.error('トラック停止エラー:', e);
          }
        });
      } catch (e) {
        console.error('ストリーム停止エラー:', e);
      } finally {
        streamRef.current = null;
      }
    }
    
    if (videoRef.current) {
      try {
        // ビデオ要素のストリーム参照をクリア
        videoRef.current.srcObject = null;
        videoRef.current.load(); // メディア要素をリセット
      } catch (e) {
        console.error('ビデオ要素リセットエラー:', e);
      }
    }
    
    setCameraActive(false);
  };

  //
  //IndexedDBをクリア
  useEffect(() => {
    const clearData = async () => {
      try {
        // 裏面撮影モードの場合はクリアしない
        const backSideCaptureMode = await indexedDBStorage.getItem('backSideCaptureMode');
        if (backSideCaptureMode === 'true') {
          return;
        }

        // 通常の名刺取り込み時は裏面画像もクリア
        await indexedDBStorage.removeItem('scanResult');
        await indexedDBStorage.removeItem('editResultBackup');
        await indexedDBStorage.removeItem('backSideImage');
        await indexedDBStorage.removeItem('isManualInput');
        // 通常撮影フローでは本登録モードフラグを残さない（仮登録顧客の編集フロー専用のため）
        await indexedDBStorage.removeItem('permanentRegistrationMode');
      } catch (error) {
        console.error('Failed to clear IndexedDB data:', error);
      }
    };

    clearData();
  }, []);
  
  // 利用可能なカメラデバイスを取得する関数
  const getAvailableCameras = async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return [];
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
      return videoDevices;
    } catch (error) {
      return [];
    }
  };
  
  // カメラを開始する関数
  const startCamera = async () => {
    // Flutter環境の場合はWebカメラを起動しない
    if (isInFlutter) {
      setLoading(false);
      setCameraActive(false);
      return;
    }

    // ブラウザ：使用直前にカメラ権限を確認する。拒否済みなら案内モーダルを表示し起動しない。
    const permitted = await ensureMediaPermission('camera');
    if (!permitted) {
      setLoading(false);
      setCameraActive(false);
      setCameraPermissionDenied(true);
      return;
    }

    // 既存のストリームがあれば停止
    stopCamera();
    
    setLoading(true);
    setCameraPermissionDenied(false);
    setApiError(null);
    
    
    try {
      // カメラの制約を設定
      let constraints: MediaStreamConstraints;
      
      if (isMobile) {
        // iOSの場合は常に複数カメラがあると仮定
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        if (isIOS && !hasMultipleCameras) {
          setHasMultipleCameras(true);
        }
        
        // 保存されたカメラモードを使用
        constraints = {
          video: {
            // できるだけ確実に指定したカメラを使うように
            facingMode: { exact: cameraFacingMode },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        };
      } else {
        // PCでは利用可能なカメラを確認
        const videoDevices = await getAvailableCameras();
        
        if (videoDevices.length > 0) {
          // メインカメラ（通常は最初のデバイス）を使用
          constraints = {
            video: {
              deviceId: { exact: videoDevices[0].deviceId },
              width: { ideal: 1280 },
              height: { ideal: 720 }
            },
            audio: false
          };
        } else {
          // カメラが見つからない場合は単純な制約
          constraints = {
            video: true,
            audio: false
          };
        }
      }
      
      // カメラストリームを取得
      let newStream;
      
      if (isMobile) {
        // モバイルの場合、exactで失敗したらidealにフォールバックする
        try {
          // まずexact制約で試す
          newStream = await navigator.mediaDevices.getUserMedia(constraints);
        } catch (error) {
          
          // ideal制約に変更して再試行
          const idealConstraints = {
            video: {
              facingMode: { ideal: cameraFacingMode },
              width: { ideal: 1280 },
              height: { ideal: 720 }
            },
            audio: false
          };
          
          newStream = await navigator.mediaDevices.getUserMedia(idealConstraints);
        }
        
        // 実際にどのカメラが選択されたか確認
        const actualTrack = newStream.getVideoTracks()[0];
        if (actualTrack && actualTrack.getSettings) {
          const settings = actualTrack.getSettings();
          
          // 実際に使用されているカメラのfacingModeを状態に保存
          if (settings.facingMode) {
            if (settings.facingMode !== cameraFacingMode) {
              setCameraFacingMode(settings.facingMode);
              
              // IndexedDBにも保存
              indexedDBStorage.setItem('cameraFacingMode', settings.facingMode)
                .catch(err => console.error('カメラモード保存エラー:', err));
            }
          }
        }
      } else {
        // 通常の方法でカメラを取得
        newStream = await navigator.mediaDevices.getUserMedia(constraints);
      }
      
      // streamRef を更新
      streamRef.current = newStream;
      
      // ビデオ要素にストリームを設定
      if (videoRef.current) {
        videoRef.current.srcObject = newStream;
        
        // onloadedmetadata イベントを使用して、メタデータが読み込まれたら再生を開始
        videoRef.current.onloadedmetadata = async () => {
          try {
            if (videoRef.current) {
              // 遅延を入れて再生を試みる
              await new Promise(resolve => setTimeout(resolve, 100));
              await videoRef.current.play();
              setCameraActive(true);
            }
          } catch (playError) {
            message.error('カメラの映像を表示できません');
            setCameraPermissionDenied(true);
          } finally {
            setLoading(false);
          }
        };
      } else {
        setLoading(false);
        setCameraPermissionDenied(true);
      }
    } catch (error) {
      setCameraPermissionDenied(true);
      setLoading(false);
      message.error('カメラへのアクセスができませんでした');
    }
  };
  
  // OpenCV.jsの読み込みを検出
  useEffect(() => {
    const checkOpenCVLoaded = () => {
      // OpenCVが既にロードされているかチェック
      if (window.cv && window.cv.getBuildInformation) {
        setCvLoaded(true);
        return true;
      }
      return false;
    };

    // 最初にチェック
    if (!checkOpenCVLoaded()) {
      
      // opencv.jsのロードが完了したらイベントリスナーを設定
      const onOpenCVLoaded = () => {
        if (checkOpenCVLoaded()) {
          document.removeEventListener('opencv-loaded', onOpenCVLoaded);
        }
      };
      
      // カスタムイベントリスナーを追加
      document.addEventListener('opencv-loaded', onOpenCVLoaded);
      
      // バックアップとしてポーリングも使用
      const timeout = setTimeout(() => {
        if (!checkOpenCVLoaded()) {
          message.error('通信環境が良いところでお試しください。');
        }
      }, 10000); // 10秒後にタイムアウト
      
      const interval = setInterval(() => {
        if (checkOpenCVLoaded()) {
          clearInterval(interval);
          clearTimeout(timeout);
        }
      }, 500);
      
      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
        document.removeEventListener('opencv-loaded', onOpenCVLoaded);
      };
    }
  }, []);
  
  // 自動撮影モード
  // 連続(multi)取り込み時は「1枚撮影 → 2秒後に次を撮影」を繰り返す。
  // captureImage(multi) が capturedImages を増やすと、その length 変化でこの effect が
  // 再実行され、次の 2秒タイマーが仕掛けられてループする。
  // single 時は capturedImages が増えないため再発火せず、1回の撮影で終わる（従来どおり）。
  useEffect(() => {
    // ローカル変数にstateを保存（クロージャの問題を避けるため）
    const currentAutoCapture = autoCapture;
    const currentCameraActive = cameraActive;
    const currentIsLandscape = isLandscape;
    const currentIsTablet = isTablet;

    // 横向きモードでは自動撮影を停止（タブレットは横向きでも自動撮影を許可）
    const shouldStopAutoCapture = currentIsLandscape && !currentIsTablet;

    // 自動撮影を開始する条件
    if (currentAutoCapture && currentCameraActive && !shouldStopAutoCapture) {

      // 自動撮影実行関数
      const doAutoCapture = () => {
        // 現在の状態を再度確認
        const stillAutoCapture = autoCapture;
        const stillCameraActive = cameraActive;
        // タブレットかどうかにかかわらず、カメラアクティブかつ自動撮影モードなら撮影
        const canCapture = stillAutoCapture && stillCameraActive &&
                          (isTablet || !isLandscape); // タブレットは常に撮影可能、それ以外は縦向きのみ

        if (canCapture) {
          captureImage(); // 撮影実行（multi時は capturedImages に蓄積されカメラは継続）
          message.success('自動撮影しました');
        } else {
        }
      };

      // 1枚撮影 → 3秒後に撮影可能。連続(multi)モードでは capturedImages.length の
      // 変化で再実行されて次のタイマーが仕掛けられ、ループする。
      const timer = setTimeout(doAutoCapture, 3000);
      return () => clearTimeout(timer);
    }
  }, [autoCapture, cameraActive, isLandscape, isTablet, capturedImages.length]);

  // 設定読み込み完了フラグを設定
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // iPad/タブレット判定を先に実行
        const userAgent = navigator.userAgent || navigator.vendor;
        
        // iPad OSの検出（iPadOS 13以降はMacOSとして報告される）
        const isIpadOS = (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) || 
                       /iPad/i.test(userAgent) ||
                       /iPad/.test(navigator.platform);
        
        if (isIpadOS || window.innerWidth >= 768) {
          setIsTablet(true);
        }
        
        // 設定読み込みを待機
        await new Promise(resolve => setTimeout(resolve, 500));
        setSettingsLoaded(true);
      } catch (error) {
        console.error('設定読み込み待機エラー:', error);
        setSettingsLoaded(true); // エラーでも起動できるようにする
      }
    };
    
    loadSettings();
  }, []);
  
  // コンポーネントマウント時にカメラを開始 - 設定読み込み後
  useEffect(() => {
    // 設定が読み込まれるまで待機
    if (!settingsLoaded) {
      return;
    }
    
    let mounted = true;
    
    const initCamera = async () => {
      try {
        
        // Flutter環境の場合はWebカメラを初期化しない
        if (isInFlutter) {
          setLoading(false);
          return;
        }
        
        // DOM要素が確実に描画された後に実行
        await new Promise(resolve => setTimeout(resolve, 300));
        
        if (mounted) {
          startCamera();
        }
      } catch (error) {
        console.error('カメラ初期化エラー:', error);
        if (mounted && !isInFlutter) {
          startCamera(); // エラーが発生しても、とりあえずカメラは起動する（Flutter環境以外）
        }
      }
    };
    
    initCamera();
    
    // クリーンアップ関数
    return () => {
      mounted = false;
      stopCamera();
    };
  }, [settingsLoaded, isMobile, cameraFacingMode]); // 設定読み込み、モバイル状態、カメラモード変更時に再実行
  
  // 名刺部分を検出して抽出する関数
  const extractBusinessCard = (imageData: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!cvLoaded || !window.cv) {
        console.error("OpenCVが読み込まれていません");
        resolve(imageData); // OpenCVが使えない場合は元の画像を返す
        return;
      }
      
      try {
        // 画像をロード
        const img = new Image();
        // 画像のロード処理は安全に行う
        img.onload = () => {
          // 最も外側のtryブロック - すべての処理を囲む
          try {
            // 一時キャンバスを作成
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = img.width;
            tempCanvas.height = img.height;
            const tempCtx = tempCanvas.getContext('2d');
            if (tempCtx === null) {
              reject(new Error('Failed to get canvas context'));
              return;
            }
            
            // TypeScriptのNull安全性のためにasを使用
            const safeTempCtx = tempCtx as CanvasRenderingContext2D;
            
            // 画像をキャンバスに描画
            safeTempCtx.drawImage(img, 0, 0, img.width, img.height);
            
            // OpenCV処理を行うブロック
            try {
              // OpenCVが使用可能かチェック
              if (!window.cv || typeof window.cv !== 'object') {
                resolve(imageData); // OpenCVが使えない場合は元の画像を返す
                return;
              }
            
              // OpenCVのMatオブジェクトを作成
              const imgData = safeTempCtx.getImageData(0, 0, img.width, img.height);
              const src = window.cv.matFromImageData(imgData);
              
              // もし画像が非常に大きい場合はリサイズ（処理速度向上のため）
              let processedSrc = src;
              const maxDimension = 1200; // 最大処理サイズ
              
              if (Math.max(src.rows, src.cols) > maxDimension) {
                processedSrc = new window.cv.Mat();
                const scale = maxDimension / Math.max(src.rows, src.cols);
                const newWidth = Math.round(src.cols * scale);
                const newHeight = Math.round(src.rows * scale);
                window.cv.resize(src, processedSrc, new window.cv.Size(newWidth, newHeight), 0, 0, window.cv.INTER_AREA);
                // 元の画像は解放
                src.delete();
              } else {
                processedSrc = src;
              }
              
              // 処理用の行列を作成
              const dst = new window.cv.Mat();
              const gray = new window.cv.Mat();
              const thresh = new window.cv.Mat();
              const contours = new window.cv.MatVector();
              const hierarchy = new window.cv.Mat();
              
              try {
                // より単純なアプローチで名刺検出を実装
                
                // グレースケール変換
                window.cv.cvtColor(processedSrc, gray, window.cv.COLOR_RGBA2GRAY);
                
                // ノイズ除去（ガウシアンブラー）
                // カーネルサイズ (3,3) でぼかし処理
                window.cv.GaussianBlur(gray, gray, new window.cv.Size(3, 3), 0);
                
                // エッジ検出用の一時行列を作成
                const edges = new window.cv.Mat();
                
                // Canny法によるエッジ検出
                // パラメーター: 入力画像, 出力画像, 最小閾値, 最大閾値, アパーチャサイズ(ソーベルカーネルのサイズ)
                // 薄い背景の名刺に対応するため閾値を調整
                const minThreshold = 0;  // 低い値でより多くのエッジを検出
                const maxThreshold = 200; // 高い値でより少ないエッジを検出
                window.cv.Canny(gray, edges, minThreshold, maxThreshold, 3);
                
                // エッジの膨張処理のための構造要素（カーネル）を作成
                const dilationKernel = window.cv.getStructuringElement(
                  window.cv.MORPH_RECT,  // 矩形の構造要素
                  new window.cv.Size(3, 3) // 3x3サイズのカーネル
                );
                
                // 膨張処理用の出力行列
                const dilatedEdges = new window.cv.Mat();
                
                // エッジを膨張させる（細い線や途切れた部分を太くして繋げる）
                window.cv.dilate(
                  edges,         // 入力画像（Cannyで検出したエッジ）
                  dilatedEdges,  // 出力画像
                  dilationKernel, // 構造要素
                  new window.cv.Point(-1, -1), // アンカーポイント（デフォルト）
                  1,             // 反復回数
                  window.cv.BORDER_CONSTANT, // ボーダータイプ
                  window.cv.morphologyDefaultBorderValue() // ボーダー値
                );
                
                // 輪郭検出
                const contours = new window.cv.MatVector();
                const hierarchy = new window.cv.Mat();
                
                // 膨張されたエッジから輪郭を検出
                window.cv.findContours(
                  dilatedEdges,     // 入力画像（膨張処理済みのエッジ）
                  contours,        // 検出された輪郭
                  hierarchy,       // 輪郭の階層情報
                  window.cv.RETR_TREE,  // 階層構造を全て取得
                  window.cv.CHAIN_APPROX_SIMPLE // 輪郭の圧縮方法（直線部分は端点のみ）
                );
                
                
                // 四角形の輪郭を格納する配列
                const rectangles = new window.cv.MatVector();
                
                // 各輪郭を処理
                for (let i = 0; i < contours.size(); i++) {
                  const contour = contours.get(i);
                  
                  // 輪郭の周囲長を計算
                  const arcLength = window.cv.arcLength(contour, true);
                  
                  // 近似精度（周囲長の2%）
                  const epsilon = 0.02 * arcLength;
                  
                  // 輪郭を多角形に近似
                  const approx = new window.cv.Mat();
                  window.cv.approxPolyDP(contour, approx, epsilon, true);
                  
                  // 頂点が4つの場合（四角形のみ）を抽出
                  if (approx.rows === 4) {
                    // 面積を計算（ノイズ除去のため、一定以上の面積のものだけを対象にする）
                    const area = window.cv.contourArea(approx);
                    if (area > 1000) { // 小さすぎる四角形は除外
                      // MatVectorに追加
                      rectangles.push_back(approx);
                    }
                  }
                  
                  // 不要になった行列を解放
                  approx.delete();
                }
                
                
                // 面積が大きい順にソートするための配列
                const rectangleAreas = [];
                for (let i = 0; i < rectangles.size(); i++) {
                  const rect = rectangles.get(i);
                  const area = window.cv.contourArea(rect);
                  rectangleAreas.push({ index: i, area: area });
                }
                
                // 面積の大きい順にソート
                rectangleAreas.sort((a, b) => b.area - a.area);
                
                // デバッグ用：元の画像に最大の四角形輪郭を描画して表示
                const contoursImage = processedSrc.clone(); // 元画像をコピー
                
                // RGBに変換（グレースケールだと緑色が表示されないため）
                if (contoursImage.channels() === 1) {
                  window.cv.cvtColor(contoursImage, contoursImage, window.cv.COLOR_GRAY2BGR);
                }
                
                // 検出された四角形がある場合、最大の四角形のみを描画
                if (rectangles.size() > 0 && rectangleAreas.length > 0) {
                  // 最大の四角形のインデックスを取得
                  const largestRectIndex = rectangleAreas[0].index;
                  const largestRect = rectangles.get(largestRectIndex);
                  
                  // 緑色で描画
                  const color = new window.cv.Scalar(0, 255, 0); // 緑色
                  
                  window.cv.drawContours(
                    contoursImage,  // 描画対象の画像
                    rectangles,    // 検出された四角形
                    largestRectIndex, // 最大の四角形のみ描画
                    color,         // 色（緑）
                    3,             // 線の太さ
                    window.cv.LINE_8  // 線の種類
                  );
                  
                  // 射影変換のための処理 - 最大の四角形の4点を取得
                  const points = [];
                  for (let i = 0; i < 4; i++) {
                    points.push({
                      x: largestRect.data32S[i * 2],
                      y: largestRect.data32S[i * 2 + 1]
                    });
                  }
                  
                  
                  // Y座標でソートして上下に分ける
                  points.sort((a, b) => a.y - b.y);
                  const topTwo = points.slice(0, 2);
                  const bottomTwo = points.slice(2, 4);
                  
                  // 上側の2点をX座標でソート（左右に分ける）
                  topTwo.sort((a, b) => a.x - b.x);
                  const topLeft = topTwo[0];
                  const topRight = topTwo[1];
                  
                  // 下側の2点をX座標でソート（左右に分ける）
                  bottomTwo.sort((a, b) => a.x - b.x);
                  const bottomLeft = bottomTwo[0];
                  const bottomRight = bottomTwo[1];
                  
                  // 頂点の順序を整える
                  const orderedPoints = [topLeft, topRight, bottomRight, bottomLeft];
                  
                  // 縦横の長さを計算
                  const width1 = Math.sqrt(Math.pow(topRight.x - topLeft.x, 2) + Math.pow(topRight.y - topLeft.y, 2));
                  const width2 = Math.sqrt(Math.pow(bottomRight.x - bottomLeft.x, 2) + Math.pow(bottomRight.y - bottomLeft.y, 2));
                  const width = Math.max(width1, width2);
                  
                  const height1 = Math.sqrt(Math.pow(bottomLeft.x - topLeft.x, 2) + Math.pow(bottomLeft.y - topLeft.y, 2));
                  const height2 = Math.sqrt(Math.pow(bottomRight.x - topRight.x, 2) + Math.pow(bottomRight.y - topRight.y, 2));
                  const height = Math.max(height1, height2);
                  
                  // 最大長を500pxに設定
                  const maxDimension = 500;
                  const scale = maxDimension / Math.max(width, height);
                  const targetWidth = Math.round(width * scale);
                  const targetHeight = Math.round(height * scale);
                  
                  // 射影変換用の行列を作成
                  const srcPoints = window.cv.matFromArray(4, 1, window.cv.CV_32FC2, [
                    topLeft.x, topLeft.y,
                    topRight.x, topRight.y,
                    bottomRight.x, bottomRight.y,
                    bottomLeft.x, bottomLeft.y
                  ]);
                  
                  const dstPoints = window.cv.matFromArray(4, 1, window.cv.CV_32FC2, [
                    0, 0,
                    targetWidth, 0,
                    targetWidth, targetHeight,
                    0, targetHeight
                  ]);
                  
                  // 透視変換行列を計算
                  const perspectiveMatrix = window.cv.getPerspectiveTransform(srcPoints, dstPoints);
                  
                  // 透視変換を適用
                  const warpedImage = new window.cv.Mat();
                  window.cv.warpPerspective(
                    processedSrc,
                    warpedImage,
                    perspectiveMatrix,
                    new window.cv.Size(targetWidth, targetHeight)
                  );
                  
                  // 変換後の画像をキャンバスに表示
                  const warpedDisplayCanvas = document.createElement('canvas');
                  warpedDisplayCanvas.width = targetWidth;
                  warpedDisplayCanvas.height = targetHeight;
                  window.cv.imshow(warpedDisplayCanvas, warpedImage);
                  
                  // 変換後の画像のURLを取得して保存
                  const warpedImageUrl = warpedDisplayCanvas.toDataURL('image/jpeg');
                  
                  // リソースを解放
                  srcPoints.delete();
                  dstPoints.delete();
                  perspectiveMatrix.delete();
                  warpedImage.delete();
                  
                  // 早期リターンして処理を中断、射影変換後の画像を返す
                  resolve(warpedImageUrl);
                  
                  // 以下の処理は実行されない
                  edges.delete();
                  dilatedEdges.delete();
                  dilationKernel.delete();
                  contours.delete();
                  hierarchy.delete();
                  rectangles.delete();
                  contoursImage.delete();
                  return;
                }
                
                // 四角形が見つからなかった場合は元の輪郭描画画像を表示
                const contoursDisplayCanvas = document.createElement('canvas');
                contoursDisplayCanvas.width = contoursImage.cols;
                contoursDisplayCanvas.height = contoursImage.rows;
                window.cv.imshow(contoursDisplayCanvas, contoursImage);
                
                // 輪郭を描画した画像のURLを取得して保存
                const contoursImageUrl = contoursDisplayCanvas.toDataURL('image/jpeg');
                
                // リソースを解放
                edges.delete();
                dilatedEdges.delete();
                dilationKernel.delete();
                contours.delete();
                hierarchy.delete();
                rectangles.delete();
                contoursImage.delete();
                
                // 早期リターンして処理を中断、輪郭検出後の画像を返す
                resolve(contoursImageUrl);
                return; // ここで関数実行を終了
                
              } finally {
                // 全てのリソースを解放 - エラー処理を追加
                try {
                  if (processedSrc && typeof processedSrc.delete === 'function') {
                    processedSrc.delete(); // リサイズした画像の場合
                  }
                  
                  if (dst && typeof dst.delete === 'function') {
                    dst.delete();
                  }
                  
                  if (gray && typeof gray.delete === 'function') {
                    gray.delete();
                  }
                  
                  if (thresh && typeof thresh.delete === 'function') {
                    thresh.delete();
                  }
                  
                  if (contours && typeof contours.delete === 'function') {
                    contours.delete();
                  }
                  
                  if (hierarchy && typeof hierarchy.delete === 'function') {
                    hierarchy.delete();
                  }
                } catch (e) {
                }
              }
            } catch (cvError) {
              resolve(imageData); // エラー時は元の画像を返す
            }
          } catch (outerError) {
            resolve(imageData); // エラー時は元の画像を返す
          }
        };
        
        img.onerror = () => {
          resolve(imageData); // エラー時は元の画像を返す
        };
        
        img.src = imageData;
      } catch (error) {
        resolve(imageData); // エラー時は元の画像を返す
      }
    });
  };

  // 連続撮影モード用: OpenCV 補正をかけてサイズ妥当性をチェック
  const processOpenCVForMulti = async (imageData: string): Promise<string | null> => {
    if (!cvLoaded) return null;
    try {
      const extracted = await extractBusinessCard(imageData);
      if (!extracted || extracted === imageData) return null;
      return await new Promise<string | null>((resolve) => {
        const img = new Image();
        img.onload = () => {
          const isValidSize = img.width > 200 && img.height > 100;
          const isAspectRatioValid =
            (img.height > img.width && img.height / img.width > 1.5) ||
            (img.height < img.width && img.width / img.height > 1.5);
          const origImg = new Image();
          origImg.onload = () => {
            const areaRatio = (img.width * img.height) / (origImg.width * origImg.height);
            const ok = isValidSize && isAspectRatioValid && areaRatio >= 0.1;
            resolve(ok ? extracted : null);
          };
          origImg.onerror = () => resolve(isValidSize ? extracted : null);
          origImg.src = imageData;
        };
        img.onerror = () => resolve(null);
        img.src = extracted;
      });
    } catch {
      return null;
    }
  };

  const captureImage = () => {
    if (!videoRef.current || !canvasRef.current || !cameraActive) return;

    try {
      const video = videoRef.current;
      const canvas = canvasRef.current;

      // キャプチャ画像を最大1920×1080にリサイズ
      const originalWidth = video.videoWidth || 640;
      const originalHeight = video.videoHeight || 480;
      // 縦向き端末を想定: 縦(高さ)1920px, 横(幅)1080pxを最大値とする
      const maxWidth = 1080;
      const maxHeight = 1920;
      const widthRatio = maxWidth / originalWidth;
      const heightRatio = maxHeight / originalHeight;
      const ratio = Math.min(widthRatio, heightRatio, 1);
      const targetWidth = Math.round(originalWidth * ratio);
      const targetHeight = Math.round(originalHeight * ratio);
      canvas.width = targetWidth;
      canvas.height = targetHeight;

      // 映像を描画（リサイズ）
      const context = canvas.getContext('2d');
      if (context === null) return;
      // Type assertionを使用してnullチェックを行った後も安全に使用
      const safeContext = context as CanvasRenderingContext2D;
      safeContext.drawImage(video, 0, 0, targetWidth, targetHeight);

      // Base64データを取得
      const imageData = canvas.toDataURL('image/jpeg', 0.95);

      // 連続撮影モード: 画像を配列に蓄積し、カメラは止めずに次の撮影に備える
      if (captureModeRef.current === 'multi') {
        processOpenCVForMulti(imageData).then((extracted) => {
          setCapturedImages((prev) => {
            const next = [...prev, { original: imageData, extracted }];
            message.success(`撮影しました (${next.length}枚)`);
            return next;
          });
        });
        return;
      }

      setCapturedImage(imageData);
      
      // OpenCVが読み込まれていて、名刺部分を抽出する
      if (cvLoaded) {
        message.loading('名刺を検出中...', 1);
        extractBusinessCard(imageData)
          .then(extractedImageData => {
            // 抽出された画像のサイズをチェック
            const checkImageSize = (imgData: string): Promise<{ width: number, height: number, isValid: boolean }> => {
              return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => {
                  // 最小サイズを定義（例: 100x100ピクセル以下は小さすぎると判断）
                  const minWidth = 200;
                  const minHeight = 100;
                  const isValid = img.width > minWidth && img.height > minHeight;

                  //
                  //縦横比チェック
                  let isAspectRatioValid = false;
                  if((img.height >img.width && img.height / img.width > 1.5) || (img.height < img.width && img.width / img.height > 1.5)){
                    isAspectRatioValid = true;
                  }
                  
                  // 元の画像に対する面積比率を計算
                  const checkOriginalSize = () => {
                    const origImg = new Image();
                    origImg.onload = () => {
                      const originalArea = origImg.width * origImg.height;
                      const extractedArea = img.width * img.height;
                      const areaRatio = extractedArea / originalArea;

                      // 20%未満は異常に小さいと判断
                      const isTooSmall = areaRatio < 0.1;
                      resolve({
                        width: img.width,
                        height: img.height,
                        isValid: isValid && isAspectRatioValid && !isTooSmall
                      });
                    };
                    origImg.onerror = () => {
                      // 元画像の読み込みに失敗した場合は単純なサイズチェックのみを返す
                      resolve({
                        width: img.width,
                        height: img.height,
                        isValid: isValid
                      });
                    };
                    origImg.src = imageData;
                  };
                  
                  checkOriginalSize();
                };
                img.onerror = () => {
                  // エラー時は無効と判断
                  resolve({ width: 0, height: 0, isValid: false });
                };
                img.src = extractedImageData;
              });
            };
            
            // 画像サイズをチェックして適切な画像を設定
            checkImageSize(extractedImageData).then(({ width, height, isValid }) => {
              if (isValid) {
                setExtractedImage(extractedImageData);
                message.success('名刺を検出・補正しました');
              } else {
                message.warning('名刺の検出サイズが小さすぎるため、元の画像を使用します');
                setExtractedImage(null);
              }
            });
          })
          .catch(error => {
            message.warning('名刺の検出に失敗しました。元の画像を使用します');
            setExtractedImage(null);
          });
      } else {
        setExtractedImage(null);
      }
      
      // カメラを停止
      stopCamera();
    } catch (error) {
      message.error('画像のキャプチャに失敗しました');
    }
  };
  
  const retakePhoto = () => {
    setCapturedImage(null);
    setExtractedImage(null);
    setApiError(null);
    startCamera();
  };
  
  // 画像を一時ファイルとして保存し、AIサーバーに送信
  const uploadImageToServer = async (imageData: string): Promise<any> => {
    // 通信環境が悪い場合に「画像を処理中...」のまま固まらないよう、
    // アップロード〜OCRの一連の通信に合計30秒のタイムアウトを設ける。
    // 超過したら AbortController で中断し、呼び出し側の再撮影フローへ倒す。
    const OCR_UPLOAD_TIMEOUT_MS = 30000; // 30秒
    const abortController = new AbortController();
    // キャンセルボタンから中断できるよう ref に保持する
    uploadAbortRef.current = abortController;
    uploadCancelledByUserRef.current = false;
    let didTimeout = false;
    const timeoutId = setTimeout(() => {
      didTimeout = true;
      abortController.abort();
    }, OCR_UPLOAD_TIMEOUT_MS);
    try {
      // API呼び出し前にトークンをリフレッシュ
      /*
      const refreshResult = await refreshToken();
      if (!refreshResult.success) {
        throw new Error('認証トークンの更新に失敗しました。再度ログインしてください。');
      }
        */

      // Base64データからBlobに変換
      const byteString = atob(imageData.split(',')[1]);
      const mimeString = imageData.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeString });
      
      // FormDataを作成
      const formData = new FormData();
      formData.append('image', blob, 'captured_image.jpg');
      formData.append('action', 'upload');
      
      // 画像をPHPサーバーにアップロード
      const response = await axios.post(API_PATHS.UPLOAD_IMAGE, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        signal: abortController.signal
      });
      
      if (!response.data.success) {
        throw new Error(response.data.message || 'アップロードに失敗しました');
      }
      
      // トークンを取得
      const token = await indexedDBStorage.getItem('token');
      if (!token) {
        throw new Error('認証トークンが見つかりません。再度ログインしてください。');
      }
      // ログイン中のユーザー情報を取得
      const userDataStr = await indexedDBStorage.getItem('user');
      let contactuserno = [];
      
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          if (userData && userData.userno) {
            contactuserno.push(userData.userno);
          }
        } catch (e) {
          console.error('ユーザー情報の解析に失敗:', e);
        }
      }
      
      // 設定モーダルで保存された AI解析モードを取得し、最後に使用したモードとして保存
      // （EditResultPage の再解析ボタン制御に使用する）
      const ocrModel = await getOcrModelPreference();
      await setLastOcrUsedModel(ocrModel);
      // 撮影時に確定した保持値を使用（解析と編集/フォローでモードを一致させる）
      const useChatGpt = await getEmergencyMode();

      // アップロード成功後、AIOCR APIに送信
      const ocrResponse = await axios.post(API_PATHS.PROCESS_OCR, {
        filename: response.data.filename,
        base64: imageData,
        contactuserno: JSON.stringify(contactuserno), // ユーザーIDの配列をJSON文字列として送信
        ocr_model: ocrModel, // 'high' のときバックエンドで高品質APIキーに切替
        use_chatgpt: useChatGpt // true のとき ChatGPT 用キーで解析
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: abortController.signal
      });

      return ocrResponse.data;
    } catch (error: any) {
      // エラーメッセージを取得
      let errorMessage = '画像処理中にエラーが発生しました';
      if (uploadCancelledByUserRef.current) {
        // ユーザーによるキャンセル。呼び出し側で撮影画面へ戻すため文面は補助的
        errorMessage = 'アップロードをキャンセルしました。';
      } else if (didTimeout || error.code === 'ERR_CANCELED' || (axios.isCancel && axios.isCancel(error))) {
        // タイムアウト（合計30秒超過）で中断された場合は、再撮影を促す文面に統一する
        errorMessage = '通信がタイムアウトしました。通信環境をご確認のうえ、もう一度撮影してください。';
      } else {
        // ユーザーキャンセル・タイムアウト以外の本物のAPIエラーのみログに記録する
        // （正常なキャンセル/タイムアウト操作でサーバーのエラーログを増やさない）
        logApiError('CameraPage.processImage', error, API_PATHS.PROCESS_OCR, 'POST');
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = `${error.response.data.message}`;
        } else if (error.message) {
          errorMessage = error.message;
        }
      }

      throw new Error(errorMessage);
    } finally {
      // 成功・失敗いずれの場合もタイマーを解除し、タイマーリークを防ぐ
      clearTimeout(timeoutId);
      uploadAbortRef.current = null;
    }
  };

  // アップロード（OCR）のキャンセル。進行中の通信を中断して撮影画面へ戻す。
  const handleCancelUpload = () => {
    // 通信が既に完了している場合（OCR 成功直後の保存処理中など）は uploadAbortRef が
    // null になっている。この状態でキャンセルを実行すると、成功パスの /edit 遷移と
    // 撮影画面への復帰が二重に走るため、進行中でなければ何もしない。
    if (!uploadAbortRef.current) return;
    uploadCancelledByUserRef.current = true;
    uploadAbortRef.current?.abort();
    message.destroy();
    setFlutterProcessing(false);
    setProcessingImage(false);
    setLoading(false);
    // 撮影画面に戻す（Flutter はネイティブカメラ再起動、Web はプレビュー再開）
    if (isInFlutter) {
      handleRetake();
    } else {
      retakePhoto();
    }
  };
  
  // 画像の外周を5pxほど切り取る関数
  const cropImageBorder = (imageData: string, borderSize: number = 5): Promise<string> => {
    return new Promise((resolve, reject) => {
      try {
        // 画像をロード
        const img = new Image();
        img.onload = () => {
          try {
            // 新しいサイズを計算（各辺から指定ピクセル分を削除）
            const newWidth = Math.max(img.width - (borderSize * 2), 10); // 最小幅10px
            const newHeight = Math.max(img.height - (borderSize * 2), 10); // 最小高さ10px
            
            // 一時キャンバスを作成
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = newWidth;
            tempCanvas.height = newHeight;
            const ctx = tempCanvas.getContext('2d');
            
            if (ctx === null) {
              resolve(imageData); // エラー時は元の画像を返す
              return;
            }
            
            // TypeScriptのNull安全性のためにasを使用
            const safeCtx = ctx as CanvasRenderingContext2D;
            
            // 画像をキャンバスに描画（外周を除いた部分）
            safeCtx.drawImage(
              img,
              borderSize, borderSize, // 元画像の切り取り開始位置（左上から5px）
              img.width - (borderSize * 2), img.height - (borderSize * 2), // 切り取るサイズ（両端から5pxずつ除外）
              0, 0, // キャンバスの描画開始位置
              newWidth, newHeight // キャンバスに描画するサイズ
            );
            
            // Base64形式の画像データを生成
            const croppedImageData = tempCanvas.toDataURL('image/jpeg', 0.95);
            
            resolve(croppedImageData);
          } catch (error) {
            resolve(imageData); // エラー時は元の画像を返す
          }
        };
        
        img.onerror = () => {
          resolve(imageData); // エラー時は元の画像を返す
        };
        
        img.src = imageData;
      } catch (error) {
        resolve(imageData); // エラー時は元の画像を返す
      }
    });
  };

  // 戻るボタンのハンドラー
  const handleBack = async () => {
    // 裏面撮影モードかチェック
    const backSideCaptureMode = await indexedDBStorage.getItem('backSideCaptureMode');
    if (backSideCaptureMode === 'true') {
      // 裏面撮影モードの場合はフラグを削除してEditResultPageに戻る（replaceで履歴置換）
      await indexedDBStorage.removeItem('backSideCaptureMode');
      navigate(getRoutePath('/edit'), { replace: true });
    } else {
      // 通常の場合はメニューに戻る
      navigate(getRoutePath('/menu'));
    }
  };

  const processImage = async () => {
    if (!capturedImage) return;

    try {
      // 裏面撮影モードかチェック（最初にチェック）
      const backSideCaptureMode = await indexedDBStorage.getItem('backSideCaptureMode');

      if (backSideCaptureMode === 'true') {
        try {
          // 裏面撮影モード：OCRやアップロードをスキップして画像だけ保存
          message.loading('裏面画像を保存中...', 0);

          let imageToSave = capturedImage;
          if (extractedImage) {
            imageToSave = extractedImage;
          }

          await indexedDBStorage.setItem('backSideImage', imageToSave);
          await indexedDBStorage.removeItem('backSideCaptureMode');

          message.destroy();
          message.success('裏面画像を取り込みました');
          navigate(getRoutePath('/edit'));
          return;
        } catch (error) {
          console.error('CameraPage - 裏面画像保存エラー:', error);
          message.destroy();
          message.error('裏面画像の保存に失敗しました');
          return;
        }
      }

      // 通常の名刺撮影モード
      setProcessingImage(true);
      setLoading(true);
      setApiError(null);

      message.loading('画像を処理中...', 0);

      // 処理結果を保持
      let finalImage = capturedImage;
      if (extractedImage) {
        finalImage = extractedImage;
      }

      // 画像の外周を5px切り取る処理を適用
      // finalImage = await cropImageBorder(finalImage, 5);

      // プレビュー画像を更新
      const previewImg = document.getElementById('preview-image') as HTMLImageElement;
      if (previewImg) {
        previewImg.src = finalImage;
      }

      // 処理する画像を決定
      const imageToProcess = finalImage;

      // AIOCR APIを呼び出す
      let result;
      let apiResponse;
      try {
        // サーバーに画像をアップロードしてAIOCR処理
        const response = await uploadImageToServer(imageToProcess);
        
        if (!response.success) {
          message.destroy(); // ローディングメッセージを閉じる
          setLoading(false);
          setProcessingImage(false);

          // エラーメッセージを設定して画面に表示
          setApiError(response.message || 'OCR処理に失敗しました。再度撮影してください。');

          // エラーメッセージも表示
          message.error(response.message || 'OCR処理に失敗しました');
          return;
        }
        
        result = response.data;
        apiResponse = response.apiResponse; // APIのレスポンスを取得


        // 処理結果をScanResultの形式に変換
        let scanResult: ScanResult = {
          success: true,
          data: result,
          imageUrl: result.imageUrl || imageToProcess,
          apiResponse: apiResponse // APIレスポンスも保存
        };

        if(result){
          scanResult.data!.businesscardfrontimage = imageToProcess; // 外周を切り取り済みの画像を使用
        }

        // 結果をIndexedDBに保存
        await indexedDBStorage.setItem('scanResult', JSON.stringify(scanResult));
        
        
        message.destroy(); // メッセージを閉じる
        message.success('画像の処理が完了しました');
        navigate(getRoutePath('/edit'));
      } catch (error: any) {
        if (uploadCancelledByUserRef.current) {
          // ユーザーによるキャンセル。handleCancelUpload 側で撮影画面へ戻し済み。
          uploadCancelledByUserRef.current = false;
          return;
        }
        // エラーメッセージを設定
        setApiError(error.message || 'API処理中にエラーが発生しました');


        message.destroy(); // メッセージを閉じる
        message.error('画像の処理に失敗しました');
        
        // エラー時はモックデータを使用するのではなく、エラー状態を表示
        setLoading(false);
        setProcessingImage(false);
      }
      
    } catch (error: any) {
      message.destroy(); // メッセージを閉じる
      message.error(error.message || '画像の処理中にエラーが発生しました');
      setApiError(error.message || '画像処理中にエラーが発生しました');
      setLoading(false);
      setProcessingImage(false);
    }
  };

  // 画像URLからBase64エンコードデータを取得する関数
    const getBase64FromImageUrl = async (imageUrl: string): Promise<string> => {
      try {
        // すでにBase64形式の場合はそのまま返す
        if (imageUrl.startsWith('data:image')) {
          return imageUrl;
        }
        
        // 画像URLから画像データを取得
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        
        // Blobをbase64に変換
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        // エラーの場合は元のURLを返す
        return imageUrl;
      }
    };
  
  // カメラ切替ボタン
  const switchCamera = async () => {
    if (!isMobile || !hasMultipleCameras) return;
    
    try {
      // 現在使用中のカメラの情報を取得
      let currentFacingMode = cameraFacingMode; // 現在の状態を使用
      
      // 念のためストリームからも確認
      if (streamRef.current && streamRef.current.getVideoTracks().length > 0) {
        const videoTrack = streamRef.current.getVideoTracks()[0];
        if (videoTrack && videoTrack.getSettings) {
          const settings = videoTrack.getSettings();
          if (settings && settings.facingMode) {
            currentFacingMode = settings.facingMode;
          }
        }
        
        // 全てのトラックを停止
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      
      // UIフィードバック
      setLoading(true);
      setCameraActive(false);
      message.loading('カメラを切り替えています...', 1);
      
      // 反転させるfacingMode
      const newFacingMode = currentFacingMode === 'user' ? 'environment' : 'user';
      
      // 新しいモードを状態変数に保存
      setCameraFacingMode(newFacingMode);
      
      // 同期的にIndexedDBに保存して確実に保存する
      try {
        await indexedDBStorage.setItem('cameraFacingMode', newFacingMode);
      } catch (saveError) {
        console.error('カメラモード保存エラー（切替時）:', saveError);
      }
      
      // 新しいストリームを取得
      let newStream;
      try {
        // まず exact 制約を試す
        newStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { exact: newFacingMode },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        });
      } catch (exactError) {
        // exact 制約に失敗した場合、ideal 制約を試す
        newStream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: newFacingMode },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        });
      }
      
      // 新しいストリームをセット
      if (newStream) {
        streamRef.current = newStream;
        
        if (videoRef.current) {
          videoRef.current.srcObject = null; // 一旦クリア
          videoRef.current.srcObject = newStream;
          
          videoRef.current.onloadedmetadata = async () => {
            try {
              if (videoRef.current) {
                // 念のため少し待ってから再生
                await new Promise(resolve => setTimeout(resolve, 100));
                await videoRef.current.play();
                setCameraActive(true);
                message.success('カメラを切り替えました');
                
                // カメラ切り替え成功時にIndexedDBに保存
                indexedDBStorage.setItem('cameraFacingMode', newFacingMode)
                  .catch(error => console.error('カメラモードの保存に失敗:', error));
              }
            } catch (playError) {
              console.error('カメラ再生エラー:', playError);
              message.error('カメラの再生に失敗しました');
              // 失敗しても状態をリセット
              setCameraActive(true);
            } finally {
              setLoading(false);
            }
          };
          
          // 念のためタイムアウト処理
          setTimeout(() => {
            if (loading) {
              setLoading(false);
              setCameraActive(true);
            }
          }, 3000);
        }
      } else {
        throw new Error('新しいカメラストリームを取得できませんでした');
      }
    } catch (error: any) {
      console.error('カメラ切替エラー:', error);
      message.error('カメラを切り替えられませんでした: ' + (error.message || '不明なエラー'));
      
      // 失敗した場合は元のカメラで再起動し状態をリセット
      setLoading(false);
      setCameraActive(true);
      startCamera();
    }
  };
  
  // 連続撮影モードの切替（即時切替。撮影済み画像は破棄せず保持する）
  const handleCaptureModeChange = (mode: CaptureMode) => {
    if (mode === captureMode) return;
    setCaptureMode(mode);
  };

  // 連続撮影モード: プレビュースライダーを開く（最後に撮影した画像から表示）
  const openPreview = () => {
    if (capturedImages.length === 0) return;
    setPreviewIndex(capturedImages.length - 1);
    setPreviewOpen(true);
  };

  // プレビュー削除・確認画面の選択補正・確定/アップロードは MultiImageUploadFlow に集約済み

  // 連続撮影の完了 → 確認画面を表示（単発撮影の確認画面に相当）
  const handleCompleteMultiCapture = () => {
    if (capturedImages.length === 0) return;
    stopCamera();
    setMultiConfirm(true);
  };

  // 確認画面 → 撮影に戻る（カメラ再開）
  const handleBackToCapture = () => {
    setMultiConfirm(false);
    startCamera();
  };

  // 自動撮影の切り替え
  const handleAutoCapture = (checked: boolean) => {
    setAutoCapture(checked);
    
    // IndexedDBに設定を保存
    indexedDBStorage.setItem('autoCapture', checked.toString())
      .catch(error => console.error('Failed to save autoCapture setting:', error));
    
    if (checked) {
      // タブレットで横向きの場合は特別なメッセージ
      if (isTablet && isLandscape) {
        message.info('自動撮影を有効にしました。タブレットは横向きでも撮影できます');
      } else {
        message.info('自動撮影を有効にしました。カメラを静止させてください');
      }
    } else {
      message.info('自動撮影を無効にしました');
    }
  };
  
  // スタイルを統一するために共通のCSSを定義
  const fullScreenStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    margin: 0,
    padding: 0,
    overflow: 'hidden',
    backgroundColor: '#000',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000
  };
  
  const videoStyle: React.CSSProperties = {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    backgroundColor: '#000'
  };
  
  // 戻るボタンのスタイル
  const backButtonStyle: React.CSSProperties = {
    color: 'white',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    borderColor: 'white',
    borderWidth: '2px',
    width: '44px',
    height: '44px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };
  
  // Flutterアプリ内でOCR処理中の場合
  if (flutterProcessing) {
    return (
      <div style={{
        ...fullScreenStyle,
        backgroundColor: '#f0f2f5'
      }}>
        <div style={{ textAlign: 'center', color: '#333' }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>AI補正中...</div>
          <div style={{ marginTop: 8, fontSize: 14, color: '#666' }}>補正が完了するまで6〜8秒お待ち下さい。</div>
          <div style={{ marginTop: 20 }}>
            <Button onClick={handleCancelUpload}>キャンセル</Button>
          </div>
        </div>
      </div>
    );
  }

  // Flutterアプリ内の場合
  if (isInFlutter && !capturedImage && !extractedImage) {
    return (
      <div style={{
        ...fullScreenStyle,
        backgroundColor: '#f0f2f5',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        {ocrError ? (
          // OCRエラー時の表示
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{
              backgroundColor: '#fff7e6',
              border: '1px solid #ffd591',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '20px'
            }}>
              <div style={{ color: '#666', textAlign: 'center', fontSize: '16px' }}>
                {ocrError}
              </div>
            </div>
            <Button 
              type="primary" 
              size="large"
              onClick={handleRetake}
              style={{ 
                backgroundColor: '#0693e3',
                borderColor: '#0693e3'
              }}
            >
              再撮影
            </Button>
          </div>
        ) : !cameraRequested ? (
          <>
            <Spin size="large" />
            <div style={{ color: '#333', marginTop: 16 }}>カメラを起動中...</div>
          </>
        ) : (
          <div style={{ textAlign: 'center', color: '#333' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>カメラの準備中...</div>
          </div>
        )}
      </div>
    );
  }

  // シンプルなスタイルでレイアウトを構築
  return (
    <div style={fullScreenStyle}>
      {/* 横向き時は操作不可モーダルを表示（タブレット以外） */}
      <Modal
        visible={isLandscape && isMobile && !isTablet}
        title={null}
        footer={null}
        closable={false}
        maskClosable={false}
        centered
      >
        <div style={{ textAlign: 'center', fontSize: 16 }}>
          スマートフォンを縦にして撮影してください
        </div>
      </Modal>
      {multiConfirm ? (
        <MultiImageUploadFlow
          images={capturedImages}
          onChangeImages={setCapturedImages}
          onBack={handleBackToCapture}
          backLabel="撮影に戻る"
          onComplete={() => {
            setMultiConfirm(false);
            setCapturedImages([]);
            navigate(getRoutePath('/menu'));
          }}
        />
      ) : capturedImage ? (
        // 撮影した画像を表示
        <div style={fullScreenStyle}>
          <div style={{ 
            position: 'absolute',
            top: '16px',
            left: '16px',
            zIndex: 100
          }}>
            <Button
              type="primary"
              shape="circle"
              icon={<ArrowLeftOutlined style={{ fontSize: '20px' }} />}
              onClick={handleBack}
              style={backButtonStyle}
            />
          </div>
          
          <img 
            src={extractedImage || capturedImage} 
            alt="撮影した名刺" 
            style={{ 
              maxWidth: '100%', 
              maxHeight: '80vh',
              objectFit: 'contain'
            }} 
            id="preview-image" // 画像IDを追加（DOM操作のため）
          />

          {/* 名刺検出に関する表示（非表示） */}
          {false && extractedImage && (
            <div style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              backgroundColor: 'rgba(0, 128, 0, 0.7)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '16px',
              fontSize: '14px',
              fontWeight: 'bold',
              zIndex: 100
            }}>
              名刺補正済み
            </div>
          )}
          
          {/* APIエラーがある場合にエラーメッセージを表示 */}
          {apiError && (
            <div style={{
              position: 'absolute',
              top: '70px',
              left: '16px',
              right: '16px',
              backgroundColor: 'rgba(255, 59, 48, 0.8)',
              color: 'white',
              padding: '12px',
              borderRadius: '8px',
              textAlign: 'center',
              zIndex: 100
            }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>登録エラー</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{apiError}</div>
            </div>
          )}

          {/* アップロード中のオーバーレイ（キャンセル導線つき） */}
          {processingImage && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.45)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 200
            }}>
              <Spin size="large" />
              <div style={{ color: '#fff', marginTop: 16 }}>画像を処理中...</div>
              <Button style={{ marginTop: 20 }} onClick={handleCancelUpload}>
                キャンセル
              </Button>
            </div>
          )}

          <div style={{
            position: 'absolute',
            bottom: '50px',
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: '16px'
          }}>
            <Button
              type="default"
              shape="round"
              icon={<RetweetOutlined />}
              onClick={retakePhoto}
              size="large"
              disabled={processingImage}
            >
              撮り直す
            </Button>
            <Button
              type="primary"
              shape="round"
              icon={<CheckOutlined />}
              onClick={processImage}
              loading={loading || processingImage}
              size="large"
              disabled={!!apiError}
            >
              この画像を使用
            </Button>
          </div>
        </div>
      ) : cameraPermissionDenied ? (
        // カメラアクセス拒否時
        <div style={{...fullScreenStyle, backgroundColor: '#f0f2f5'}}>
          <Result
            status="error"
            title="カメラにアクセスできません"
            subTitle="カメラへのアクセス許可を確認してください"
            extra={[
              <Button 
                type="primary" 
                key="retry" 
                icon={<ReloadOutlined />}
                onClick={startCamera}
              >
                再試行
              </Button>,
              <Button
                key="back"
                onClick={handleBack}
              >
                戻る
              </Button>
            ]}
          />
        </div>
      ) : (
        // カメラプレビュー (フルスクリーン)
        <div style={fullScreenStyle}>
          {/* ビデオ表示領域 */}
          <video 
            ref={videoRef} 
            autoPlay 
            playsInline
            muted
            style={videoStyle} 
          />
          
          {/* ローディング表示 */}
          {loading && !cameraActive && (
            <div style={{ 
              ...fullScreenStyle,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              zIndex: 10
            }}>
              <Spin size="large" />
              <div style={{ color: 'white', marginTop: 16 }}>カメラを準備中...</div>
            </div>
          )}
          
          {/* 上部のオーバーレイ UI */}
          <div style={{ 
            position: 'absolute',
            top: '16px',
            left: '16px',
            right: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            zIndex: 20
          }}>
            <Button
              type="primary"
              shape="circle"
              icon={<ArrowLeftOutlined style={{ fontSize: '20px' }} />}
              onClick={handleBack}
              style={backButtonStyle}
            />
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: 'white', textShadow: '0 0 4px rgba(0, 0, 0, 0.8)' }}>
                自動撮影 {isTablet ? '(タブレット)' : ''}
              </span>
              <Switch
                checked={autoCapture}
                onChange={handleAutoCapture}
                disabled={isLandscape && !isTablet} // タブレットは横向きでも使用可能
                className={isTablet ? "tablet-switch" : ""} // タブレット用のクラス追加
              />
            </div>
          </div>

          {/* 自動撮影モードのガイド（上部に表示し、下部ボタンと重ならないようにする） */}
          {autoCapture && (
            <div style={{
              position: 'absolute',
              top: '74px',
              left: '50%',
              transform: 'translateX(-50%)',
              maxWidth: '86%',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              padding: '8px 16px',
              borderRadius: '20px',
              color: 'white',
              textAlign: 'center',
              fontSize: '13px',
              lineHeight: '1.4',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              zIndex: 20,
              pointerEvents: 'none',
            }}>
              カメラを極力動かさないようにしてください
            </div>
          )}

          {/* 連続撮影モード: 最後に撮影した画像のサムネイル（左下・押すとプレビュースライダー） */}
          {captureMode === 'multi' && capturedImages.length > 0 && (
            <div style={{
              position: 'absolute',
              left: '20px',
              bottom: 'calc(130px + env(safe-area-inset-bottom, 0px) + var(--flutter-footer-height, 0px))',
              zIndex: 20,
            }}>
              <Badge count={capturedImages.length} overflowCount={99} offset={[-2, 2]}>
                <div
                  onClick={openPreview}
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    border: '3px solid white',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
                    cursor: 'pointer',
                  }}
                >
                  <img
                    src={
                      capturedImages[capturedImages.length - 1].extracted ||
                      capturedImages[capturedImages.length - 1].original
                    }
                    alt="最後に撮影した画像"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </div>
              </Badge>
            </div>
          )}

          {/* 撮影モード切替（1枚撮影 / 連続撮影） */}
          <div style={{
            position: 'absolute',
            bottom: 'calc(150px + env(safe-area-inset-bottom, 0px) + var(--flutter-footer-height, 0px))',
            left: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 20,
            pointerEvents: 'none', // 透明な余白部分が左下サムネイル等のタップを奪わないようにする
          }}>
            <Segmented
              value={captureMode}
              onChange={(val) => handleCaptureModeChange(val as CaptureMode)}
              options={[
                { label: '1枚撮影', value: 'single' },
                // 緊急モード（UseChatGPT=true）の間は連続撮影を選択不可にする
                { label: '連続撮影', value: 'multi', disabled: emergencyMode },
              ]}
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                padding: '2px',
                pointerEvents: 'auto',
              }}
            />
            {emergencyMode && (
              <span style={{
                marginTop: 6,
                color: '#ff4d4f',
                fontSize: 13,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                padding: '2px 8px',
                borderRadius: 4,
                pointerEvents: 'auto',
              }}>
                現在緊急モードのため、複数枚撮影は選択できません
              </span>
            )}
          </div>

          {/* 下部の撮影ボタン */}
          <div style={{
            position: 'absolute',
            bottom: 'calc(28px + env(safe-area-inset-bottom, 0px) + var(--flutter-footer-height, 0px))',
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '70px', // 自動撮影でシャッターが消えても行高さを保ち、左右ボタンの中心線を一定にする
            zIndex: 20,
            pointerEvents: 'none', // 透明な余白部分が完了ボタン等のタップを奪わないようにする
          }}>
            {/* カメラ切替ボタン - モバイルで複数カメラがある場合のみ表示 */}
            {isMobile && hasMultipleCameras && (
              <div style={{ position: 'absolute', left: '20px', top: 0, bottom: 0, display: 'flex', alignItems: 'center', pointerEvents: 'auto' }}>
                <Button
                  type="default"
                  shape="circle"
                  icon={<RetweetOutlined style={{ fontSize: '20px' }} />}
                  onClick={switchCamera}
                  disabled={loading || !cameraActive}
                  style={{
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    borderColor: 'white',
                    color: (loading || !cameraActive) ? 'rgba(255, 255, 255, 0.5)' : 'white',
                    width: '50px',
                    height: '50px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}
                />
              </div>
            )}
            
            {/* 自動撮影モードではシャッターを隠す（ガイドは上部に表示）。手動時のみ撮影ボタンを表示 */}
            {!autoCapture && (
              // 手動撮影ボタン
              <div
                onClick={() => {
                  // タブレットは横向きでも撮影可能
                  if (!isLandscape || isTablet) captureImage();
                }}
                style={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  // タブレットは横向きでも撮影可能
                  cursor: (isLandscape && !isTablet) ? 'not-allowed' : 'pointer',
                  opacity: (isLandscape && !isTablet) ? 0.5 : 1,
                  pointerEvents: (isLandscape && !isTablet) ? 'none' : 'auto',
                  border: '4px solid white',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div style={{
                  width: '54px',
                  height: '54px',
                  borderRadius: '50%',
                  backgroundColor: '#ff3b30', // 赤色の撮影ボタン
                  border: '2px solid rgba(255, 255, 255, 0.8)'
                }} />
              </div>
            )}

            {/* 連続撮影モード: 完了ボタン（右下・撮影行と縦中央を揃える） */}
            {captureMode === 'multi' && capturedImages.length > 0 && (
              <div style={{
                position: 'absolute',
                right: '20px',
                top: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                pointerEvents: 'auto',
              }}>
                <Button
                  type="primary"
                  shape="round"
                  size="middle"
                  onClick={handleCompleteMultiCapture}
                  style={{ height: 'auto', padding: '4px 14px', fontSize: '13px', lineHeight: '1.4' }}
                >
                  完了 ({capturedImages.length})
                </Button>
              </div>
            )}
          </div>

          <canvas ref={canvasRef} style={{ display: 'none' }} />
        </div>
      )}

      {/* 撮影/アップロード画像の拡大プレビュー（共通コンポーネント） */}
      <ImagePreviewCarouselModal
        open={previewOpen}
        images={capturedImages}
        initialIndex={previewIndex}
        onClose={() => setPreviewOpen(false)}
        onImagesChange={setCapturedImages}
      />
    </div>
  );
};

export default CameraPage;